import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthorizeGuard } from 'src/api-authorization/authorize.guard';

import { AdminGuard } from './shared/core/route-guards/admin.guard';
import { NotFoundGuard } from './shared/core/route-guards/not-found.guard';
import { UnauthorizeGuard } from 'src/api-authorization/unauthorize.guard';
import { SuperAdminGuard } from './shared/core/route-guards/super-admin.guard';
import { MfaEnrollmentGuard } from './shared/core/route-guards/mfa-enrollment.guard';



const routes: Routes = [
	{
		path: 'account',
		data: {
			metaTitle: 'Account',
		},
		loadComponent: () => import('./account/account.component').then(m => m.AccountComponent),
		children: [
			{
				path: 'login',
				loadComponent: () => import('./account/auth/login/login.component').then(m => m.LoginComponent),
				canActivate: [UnauthorizeGuard],
				data: {
					bodyClass: 'login',
					hideLoader: true,
					metaTitle: 'Login',
				},
			},
			{
				path: 'register/:subdomain/:token',
				loadComponent: () => import('./account/auth/register/register.component').then(m => m.RegisterComponent),
				canActivate: [UnauthorizeGuard],
				data: {
					bodyClass: 'register',
					metaTitle: 'Register',
				},
			},
			{
				path: 'register/:subdomain',
				loadComponent: () => import('./account/auth/register/register.component').then(m => m.RegisterComponent),
				canActivate: [UnauthorizeGuard],
				data: {
					bodyClass: 'register',
					metaTitle: 'Register',
				},
			},
			{
				path: 'logout',
				loadComponent: () => import('./account/auth/logout/logout.component').then(m => m.LogoutComponent),
				canActivate: [AuthorizeGuard],
				data: {
					metaTitle: 'Logging Out',
				},
			},
			{
				path: 'logged-out',
				loadComponent: () => import('./account/auth/logged-out/logged-out.component').then(m => m.LoggedOutComponent),
				canActivate: [UnauthorizeGuard],
				data: {
					metaTitle: 'Logged Out',
				},
			},
			{
				path: 'forgot-password/:subdomain',
				loadComponent: () => import('./account/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
				canActivate: [UnauthorizeGuard],
				data: {
					metaTitle: 'Reset Password',
				},
			},
			{
				path: 'forgot-password',
				loadComponent: () => import('./account/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
				canActivate: [UnauthorizeGuard],
				data: {
					metaTitle: 'Reset Password',
				},
			},
			{
				path: 'reset-password',
				loadComponent: () => import('./account/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
				data: {
					metaTitle: 'Reset Password',
				},
			},
			{
				path: 'manage',

				canActivate: [AuthorizeGuard],
				data: {
					metaTitle: 'Manage Account',
					disableSiteLoader: true,
				},
				loadComponent: () => import('./account/manage/manage.component').then(m => m.AccountManageComponent),

				children: [
					{
						path: 'profile',
						loadComponent: () => import('./account/manage/profile/profile.component').then(m => m.AccountProfileComponent),
						canActivate: [AuthorizeGuard],
						data: {
							metaTitle: 'Profile',
						},
					},

					{
						path: 'account',
						loadComponent: () => import('./account/manage/account/account-settings.component').then(m => m.AccountSettingsComponent),
						canActivate: [AuthorizeGuard],
						data: {
							metaTitle: 'Account Settings',
						},
					},


					{
						path: '',
						redirectTo: 'account',
						pathMatch: 'full',
					},
				],
			},
			{
				path: '',
				redirectTo: '/account/login',
				pathMatch: 'full',
			},
			{
				path: 'switch',
				loadComponent: () => import('./account/roles/search.component').then(m => m.TradingPartnerRolesSearchComponent),
			},
		],
	},

];


@NgModule({
    imports: [
        RouterModule.forRoot([...routes,

                {
                    path: 'admin/dashboard',
                    canActivate: [AuthorizeGuard, AdminGuard],
                    loadChildren: () => import('./admin/dashboard/dashboard.module').then(mod => mod.DashboardModule),
                    data: {
                        admin: true,
                        metaTitle: 'Dashboard'
                    }
                },

                {
                    path: 'admin/users',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
                    loadChildren: () => import('./admin/user/users.module').then(m => m.UsersModule),
                    data: { admin: true, metaTitle: 'Users' }
                },

                {
                    path: 'admin/trading-partners/:tradingPartnerID/zuplo',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
                    loadChildren: () => import('./admin/trading-partner-zuplo/trading-partner-zuplo.module')
                        .then(m => m.TradingPartnerZuploModule),
                    data: { admin: true, metaTitle: 'API Consumer Management' }
                },

				{
					path: 'admin/trading-partners',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard],
					loadChildren: () => import('./admin/trading-partners/trading-partners.module')
						.then(m => m.TradingPartnersModule),
					data: { admin: true, metaTitle: 'Trading Partners' }
				},

                {
                    path: 'admin/trading-partners/:tradingPartnerID/submissions/:snapshotGuid/members',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
                    loadChildren: () => import('./admin/trading-partner/submission-members/submission-members.module')
                        .then(m => m.SubmissionMembersModule),
                    data: { admin: true, metaTitle: 'Members' }
                },

                {
                    path: 'admin/trading-partners/:tradingPartnerID/submissions/:snapshotGuid/:processingInfoId',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
                    loadChildren: () => import('./admin/trading-partner/submission-detail/submission-detail.module')
                        .then(m => m.SubmissionDetailModule),
                    data: { admin: true, metaTitle: 'Processing Info Detail' }
                },

                {
                    path: 'admin/trading-partners/:tradingPartnerID/submissions/:snapshotGuid',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
                    loadChildren: () => import('./admin/trading-partner/snapshot-detail/snapshot-detail.module')
                        .then(m => m.SnapshotDetailModule),
                    data: { admin: true, metaTitle: 'Snapshot Detail' }
                },

                {
                    path: 'admin/admin-guide',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
                    loadChildren: () => import('./admin/admin-guide/admin-guide.module')
                        .then(m => m.AdminGuideModule),
                    data: { admin: true, metaTitle: 'Admin Guide' }
                },

                {
                    path: 'admin/error-logs',
                    canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
                    loadChildren: () => import('./admin/error-logs/error-logs.module')
                        .then(m => m.ErrorLogsModule),
                    data: { admin: true, metaTitle: 'Error & Event Logs' }
                },

				{
					path: 'admin/edi-transmissions',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
					loadChildren: () => import('./admin/edi-transmissions/edi-transmissions.routes')
						.then(m => m.ediTransmissionsRoutes),
					data: { admin: true, metaTitle: 'EDI Transmissions' }
				},

				{
					path: 'admin/submissions/:snapshotGuid/:processingInfoId',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
					loadChildren: () => import('./admin/trading-partner/submission-detail/submission-detail.module')
						.then(m => m.SubmissionDetailModule),
					data: { admin: true, metaTitle: 'Processing Info Detail' }
				},

				{
					path: 'admin/submissions/:snapshotGuid',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
					loadChildren: () => import('./admin/trading-partner/snapshot-detail/snapshot-detail.module')
						.then(m => m.SnapshotDetailModule),
					data: { admin: true, metaTitle: 'Snapshot Detail' }
				},

				{
					path: 'admin/submissions',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
					loadChildren: () => import('./admin/submissions/submissions.module')
						.then(m => m.SubmissionsModule),
					data: { admin: true, metaTitle: 'Submissions' }
				},

				{
					path: 'admin/reports',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
					loadChildren: () => import('./admin/reports/reports.module').then(m => m.ReportsModule),
					data: { admin: true, metaTitle: 'Reports' }
				},

				{
					path: 'admin/test-suite',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
					loadChildren: () => import('./admin/test-suite/test-suite.module')
						.then(m => m.TestSuiteModule),
					data: { admin: true, metaTitle: 'Test Suite' }
				},

				{
					path: 'admin/vendors/:familyId/pipelines',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
					loadChildren: () => import('./admin/pipelines/pipelines.module')
						.then(m => m.PipelinesModule),
					data: { admin: true, metaTitle: 'Pipeline Definition' }
				},

				{
					path: 'admin/vendors',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
					loadChildren: () => import('./admin/vendors/vendors.module').then(m => m.VendorsModule),
					data: { admin: true, metaTitle: 'Vendors' }
				},

				{
					path: 'admin/carrier-guide',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, AdminGuard],
					loadComponent: () => import('./admin/carrier-guide/carrier-guide.component')
						.then(m => m.CarrierGuideComponent),
					data: { admin: true, metaTitle: 'Carrier Guide' }
				},

				{
					path: 'admin/snapshot-validation',
					canActivate: [AuthorizeGuard, MfaEnrollmentGuard, SuperAdminGuard],
					loadChildren: () => import('./admin/snapshot-validation/snapshot-validation.module').then(m => m.SnapshotValidationModule),
					data: { admin: true, metaTitle: 'Snapshot Validation' }
				},

                {
                    path: 'styleguide',
                    loadComponent: () => import('src/app/styleguide/styleguide.component').then(m => m.StyleguideComponent),
                    data: { metaTitle: 'Style Guide' }
                },

                {
                    path: 'error',
                    loadChildren: () => import('./shared/core/error/error.module').then(mod => mod.ErrorModule),
                    data: { metaTitle: 'Error' }
                },

                {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: 'dashboard',

                },

                {
                    path: '**',
                    canActivate: [NotFoundGuard],
                    loadComponent: () => import('./shared/core/error/not-found/not-found.component').then(m => m.NotFoundComponent)
                }
            ]
            , {
                paramsInheritanceStrategy: 'always',
                // enableTracing: true
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }



        )],
	exports: [RouterModule],
})
export class AppRoutingModule {}
